import React, { useMemo, useState } from 'react';
import { Flex, Tooltip } from 'antd';
import { ButtonDropdown } from 'components/Base/ButtonDropdown';
import { Tag, TagColors } from '@packages/utils';
import { StatusType } from '../TacticTable.types';
import { ChevronDown, InfoOutline } from '../../../../../../assets/icon/icon';
import CompletedDateModal from './CompletedDateModal';

type TacticStatusDropdownProps = {
  value: StatusType;
  counts: {
    todo: number;
    inProgress: number;
    stalled: number;
    completed: number;
  };
  disabled?: boolean;
  onChange?: (status: StatusType) => void;
  onComplete?: (date: import('dayjs').Dayjs) => void;
};

const statusColorMap: Record<StatusType, TagColors> = {
  TODO: 'grey',
  IN_PROGRESS: 'primary',
  STALLED: 'danger',
  COMPLETED: 'success',
};

const statusLabels: Record<StatusType, string> = {
  TODO: 'To Do',
  IN_PROGRESS: 'In Progress',
  STALLED: 'Stalled',
  COMPLETED: 'Completed',
};

const TacticStatusDropdown = ({
  value,
  counts,
  disabled,
  onChange,
  onComplete,
}: TacticStatusDropdownProps) => {
  const [currentStatus, setCurrentStatus] = useState<StatusType>(value);
  const [isCompletedModalOpen, setIsCompletedModalOpen] = useState(false);

  const menuItems = useMemo(
    () =>
      (['TODO', 'IN_PROGRESS', 'STALLED', 'COMPLETED'] as StatusType[]).map(
        option => ({
          key: option,
          label: (
            <Tag
              color={statusColorMap[option]}
              style={{ width: 110, textAlign: 'center', marginRight: 0 }}
            >
              {statusLabels[option]}
            </Tag>
          ),
          onClick: () => {
            if (option === 'COMPLETED') {
              setIsCompletedModalOpen(true);
              return;
            }
            setCurrentStatus(option);
            onChange?.(option);
          },
          disabled,
        }),
      ),
    [disabled, onChange],
  );

  const tooltipContent = (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 4,
      }}
    >
      <div
        style={{
          fontWeight: 700,
          fontSize: 14,
          lineHeight: '20px',
          color: 'var(--Main-border, #DEDEDE)',
        }}
      >
        Tasks Progress:
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          width: '100%',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            color: 'var(--Main-border, #DEDEDE)',
            fontSize: 14,
            lineHeight: '20px',
            fontWeight: 400,
          }}
        >
          <span>Not Started:</span>
          <span>{counts.todo || 0}</span>
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            color: '#34B7E4',
            fontSize: 14,
            lineHeight: '20px',
            fontWeight: 400,
          }}
        >
          <span>In Progress:</span>
          <span>{counts.inProgress || 0}</span>
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            color: 'var(--Pink-Hover, #FC6F8E)',
            fontSize: 14,
            lineHeight: '20px',
            fontWeight: 400,
          }}
        >
          <span>Stalled:</span>
          <span>{counts.stalled || 0}</span>
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            color: 'var(--Success-Hover, #1FC16E)',
            fontSize: 14,
            lineHeight: '20px',
            fontWeight: 400,
          }}
        >
          <span>Completed:</span>
          <span>{counts.completed || 0}</span>
        </div>
      </div>
    </div>
  );

  return (
    <Flex gap={8} align="center">
      <Tag
        color={statusColorMap[currentStatus]}
        style={{ width: 110, textAlign: 'center', marginRight: 0 }}
      >
        {statusLabels[currentStatus]}
      </Tag>
      <ButtonDropdown
        buttonProps={{
          children: <ChevronDown size={16} />,
          color: 'primary',
          style: { border: '0', background: 'none', padding: 0 },
        }}
        dropdownProps={{
          menu: { items: menuItems },
          rootClassName: 'button-dropdown',
          placement: 'bottomRight',
          disabled,
        }}
      />
      <Tooltip
        title={tooltipContent}
        placement="topLeft"
        color="#3F3F41"
        overlayInnerStyle={{
          background: '#3F3F41',
          borderRadius: 14,
          padding: '12px 16px',
        }}
      >
        <div
          style={{
            lineHeight: 0,
            display: 'flex',
            alignItems: 'center',
            width: 24,
            height: 24,
            flexShrink: 0,
          }}
        >
          <InfoOutline size={24} fill="#656565" />
        </div>
      </Tooltip>

      <CompletedDateModal
        open={isCompletedModalOpen}
        onCancel={() => setIsCompletedModalOpen(false)}
        onAdd={date => {
          setCurrentStatus('COMPLETED');
          setIsCompletedModalOpen(false);
          onComplete?.(date);
        }}
      />
    </Flex>
  );
};

export default TacticStatusDropdown;
